import { Period } from 'src/app/menu/smart-review/core/utils/period.util';
import { CostExportRow } from '../interfaces/costs.interface';
import { costToCsvDecimal, currencySymbol, formatApiDate } from './costs-format.util';

/**
 * Geração do CSV de custos para o Excel pt-BR. Três detalhes que, se errados, fazem o
 * arquivo abrir como uma coluna só de texto:
 *
 * - separador `;` (o Excel pt-BR espera ponto-e-vírgula, não vírgula);
 * - decimal com vírgula, obtido por troca na string — nunca `Number(cost).toFixed()`;
 * - UTF-8 **com BOM**, senão os acentos viram `Ã§`.
 */

/** Marca de ordem de bytes. Vai como primeiro caractere do arquivo. */
const BOM = '﻿';

const SEPARATOR = ';';

const HEADER = [
  'Data',
  'Provider',
  // Valor cru, como 'Tipo' e 'Provider' ao lado: `github` e `tfvc` já se leem, e é o
  // vocabulário que volta para `?sourceProvider=` sem tradução manual. Rótulo só entra
  // onde o valor é opaco — ver Área/ID da área abaixo.
  'Origem do código',
  'Tipo',
  'Recurso',
  // Nome do review gravado na execução — sobrevive mesmo se a origem for apagada
  // depois. Vazio quando a linha é anterior a esse campo existir e a origem já sumiu.
  'Descrição',
  // Métrica do REVIEW (changeset/issue), não desta execução — o mesmo valor se repete
  // em toda linha do mesmo Recurso. Vazio para comparison/standalone (sempre 1 arquivo).
  'Fontes',
  'Tipo de prompt',
  'Solicitante',
  // Descrição e id lado a lado: a descrição é o que se lê, o id é o que permite
  // conferir a linha contra a origem quando o número não bate.
  'Área',
  'ID da área',
  'Modelos',
  'Chamadas ao LLM',
  'Tokens reportados',
  'Tokens',
  'Custo',
  'Status',
  // Ao lado do Status porque é o que ele não distingue: entre duas linhas sem custo,
  // qual teve o trace truncado no provider e qual não chegou a ter trace. Vazia nas
  // reconciliadas — ao fechar, a reconciliação limpa o motivo.
  'Motivo',
  // Última de propósito: é a coluna de procedência, não entra em tabela dinâmica.
  // Com ela dá para conferir uma linha contestada — ou um custo "partial" — contra o
  // trace de origem. Vazia nas execuções sem trace (as mesmas marcadas `untracked`).
  'Trace'
];

/** Escapa uma célula: tudo entre aspas, com aspas internas duplicadas. */
function cell(value: string | number | undefined | null): string {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

/**
 * Linha de aviso no topo do arquivo. É a contrapartida de exportar tudo: sem ela, quem
 * soma a coluna de custo inclui execuções não reconciliadas, que vêm com zero, e obtém
 * um total silenciosamente incompleto.
 */
function warningRow(): string {
  return cell(
    'ATENÇÃO: linhas com Status diferente de "reconciled" ainda não tiveram o custo apurado ' +
    'e vêm com custo 0 — exceto "partial", cujo custo é real mas só cobre parte das chamadas ' +
    'ao LLM (o provider truncou o trace), então soma menos que o gasto verdadeiro. ' +
    'Filtre por Status antes de somar a coluna Custo.'
  );
}

/** Cabeçalho com a moeda no rótulo do custo — a moeda vem da resposta, não do template. */
function headerRow(currency: string): string {
  const symbol = currencySymbol(currency);
  return HEADER
    .map(label => (label === 'Custo' && symbol ? `Custo (${symbol})` : label))
    .map(cell)
    .join(SEPARATOR);
}

function dataRow(row: CostExportRow, areaNames: Map<string, string>): string {
  return [
    cell(formatApiDate(row.date)),
    cell(row.provider),
    cell(row.sourceProvider),
    cell(row.resourceType),
    cell(row.resourceId),
    cell(row.label || ''),
    cell(row.sourceCount ?? ''),
    cell(row.promptType),
    cell(row.requestedBy),
    cell(areaNames.get(row.areaId) || ''),
    cell(row.areaId),
    cell(row.models),
    cell(row.llmCalls),
    cell(row.reportedTokens),
    cell(row.tokens),
    // Troca de ponto por vírgula na string: preserva toda a precisão que veio da API.
    cell(costToCsvDecimal(row.cost)),
    cell(row.status),
    cell(row.statusReason || ''),
    cell(row.traceId || '')
  ].join(SEPARATOR);
}

/**
 * Monta o conteúdo completo do CSV (com BOM), pronto para virar Blob.
 *
 * `areaNames` traduz o id de área que a API devolve; sem o catálogo a coluna de
 * descrição sai vazia e a do id continua preenchida — o arquivo nunca perde o dado.
 */
export function buildCostsCsv(
  rows: CostExportRow[],
  period: Period,
  currency: string,
  areaNames: Map<string, string> = new Map()
): string {
  const periodLabel = period?.dateFrom && period?.dateTo
    ? `Período: ${formatApiDate(period.dateFrom)} a ${formatApiDate(period.dateTo)}`
    : 'Período: todo o histórico';

  const lines = [
    cell(periodLabel),
    warningRow(),
    headerRow(currency),
    ...rows.map(row => dataRow(row, areaNames))
  ];

  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/** `custos-YYYY-MM-DD_a_YYYY-MM-DD.csv`, ou `custos-completo.csv` sem recorte. */
export function costsCsvFilename(period: Period): string {
  if (!period?.dateFrom || !period?.dateTo) { return 'custos-completo.csv'; }
  return `custos-${period.dateFrom}_a_${period.dateTo}.csv`;
}
